/**
 * Leviathan Web - Recent Activity Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows recent orders, invoices, and dispatches
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Activity } from 'lucide-react';
import { API_URL } from '@/config/api';

interface RecentOrder {
  id: string;
  orderNumber: string;
  status: string;
  total: number;
  createdAt: string;
  customer: { name: string };
}

interface RecentInvoice {
  id: string;
  invoiceNumber: string;
  status: string;
  total: number;
  createdAt: string;
  customer: { name: string };
}

interface RecentDispatch {
  id: string;
  dispatchNumber: string;
  status: string;
  scheduledDate: string;
  order: { customer: { name: string } } | null;
  driver: { firstName: string; lastName: string } | null;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const statusColors: Record<string, string> = {
  DRAFT: 'text-text-tertiary bg-text-tertiary/10',
  CONFIRMED: 'text-steel bg-steel/10',
  SCHEDULED: 'text-amber bg-amber/10',
  IN_PROGRESS: 'text-warning-amber bg-warning-amber/10',
  EN_ROUTE: 'text-warning-amber bg-warning-amber/10',
  DELIVERED: 'text-success-green bg-success-green/10',
  COMPLETED: 'text-success-green bg-success-green/10',
  SENT: 'text-steel bg-steel/10',
  PAID: 'text-success-green bg-success-green/10',
  PARTIAL: 'text-warning-amber bg-warning-amber/10',
  CANCELLED: 'text-error-red bg-error-red/10',
  VOID: 'text-error-red bg-error-red/10',
};

const RecentActivityWidget = () => {
  const { accessToken } = useAuth();
  const [orders, setOrders] = useState<RecentOrder[]>([]);
  const [invoices, setInvoices] = useState<RecentInvoice[]>([]);
  const [dispatches, setDispatches] = useState<RecentDispatch[]>([]);
  const [activeTab, setActiveTab] = useState<'orders' | 'invoices' | 'dispatches'>('orders');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          const activity = data.dashboard?.recentActivity;
          setOrders(activity?.orders ?? []);
          setInvoices(activity?.invoices ?? []);
          setDispatches(activity?.dispatches ?? []);
        }
      } catch {
        // silent
      }
    };
    fetchData();
  }, [accessToken]);

  const tabs = [
    { key: 'orders' as const, label: 'Orders', count: orders.length },
    { key: 'invoices' as const, label: 'Invoices', count: invoices.length },
    { key: 'dispatches' as const, label: 'Dispatches', count: dispatches.length },
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Activity className="w-4 h-4 text-steel" />
          <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
            Recent Activity
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-3 border-b border-panel-border">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider transition-colors border-b-2 ${
              activeTab === tab.key
                ? 'text-amber border-amber'
                : 'text-text-tertiary border-transparent hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-1 overflow-y-auto flex-1">
        {activeTab === 'orders' &&
          orders.map((order) => (
            <Link
              key={order.id}
              to={`/orders/${order.id}`}
              className="flex items-center justify-between py-1.5 px-2 -mx-1 rounded hover:bg-panel-border/20 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-text-primary truncate">
                  {order.orderNumber}
                </div>
                <div className="text-[10px] text-text-tertiary truncate">
                  {order.customer.name}
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <span
                  className={`px-1.5 py-0.5 text-[9px] font-mono uppercase rounded ${statusColors[order.status] || 'text-text-secondary bg-text-secondary/10'}`}
                >
                  {order.status.replace('_', ' ')}
                </span>
                <span className="text-[10px] text-text-tertiary font-mono">
                  {formatRelativeTime(order.createdAt)}
                </span>
              </div>
            </Link>
          ))}

        {activeTab === 'invoices' &&
          invoices.map((inv) => (
            <Link
              key={inv.id}
              to={`/invoices/${inv.id}`}
              className="flex items-center justify-between py-1.5 px-2 -mx-1 rounded hover:bg-panel-border/20 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-text-primary truncate">
                  {inv.invoiceNumber}
                </div>
                <div className="text-[10px] text-text-tertiary truncate">
                  {inv.customer.name}
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <span
                  className={`px-1.5 py-0.5 text-[9px] font-mono uppercase rounded ${statusColors[inv.status] || 'text-text-secondary bg-text-secondary/10'}`}
                >
                  {inv.status.replace('_', ' ')}
                </span>
                <span className="text-[10px] text-text-tertiary font-mono">
                  {formatRelativeTime(inv.createdAt)}
                </span>
              </div>
            </Link>
          ))}

        {activeTab === 'dispatches' &&
          dispatches.map((d) => (
            <Link
              key={d.id}
              to={`/dispatch/${d.id}`}
              className="flex items-center justify-between py-1.5 px-2 -mx-1 rounded hover:bg-panel-border/20 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-text-primary truncate">
                  {d.dispatchNumber}
                </div>
                <div className="text-[10px] text-text-tertiary truncate">
                  {d.driver
                    ? `${d.driver.firstName} ${d.driver.lastName}`
                    : 'Unassigned'}
                </div>
              </div>
              <div className="flex items-center space-x-2 ml-2">
                <span
                  className={`px-1.5 py-0.5 text-[9px] font-mono uppercase rounded ${statusColors[d.status] || 'text-text-secondary bg-text-secondary/10'}`}
                >
                  {d.status.replace('_', ' ')}
                </span>
                <span className="text-[10px] text-text-tertiary font-mono">
                  {formatRelativeTime(d.scheduledDate)}
                </span>
              </div>
            </Link>
          ))}
      </div>
    </div>
  );
};

export default RecentActivityWidget;
